import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  TextField,
  Button,
  Card,
  Typography,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Modal,
  InputAdornment,
  IconButton,
  Autocomplete,
  FormControl,
  Avatar,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import axios from "../../api";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import { Formik, Form, FormikHelpers } from "formik";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import Locations from "examples/ReuseFunctions/Locations";
import * as Yup from "yup";
import {
  Visibility,
  VisibilityOff,
  Edit as EditIcon,
} from "@mui/icons-material";
import SoftTypography from "components/SoftTypography";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import { AxiosError } from "axios";
import type { RootState } from "../../store/index";
import { useThemeColor } from "context/ThemeContext";
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      "Invalid email format"
    )
    .typeError("Enter a valid email")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Enter a valid phone number")
    .required("Phone number is required"),
});
const addressValidationSchema = Yup.object({
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Region is required"),
  address: Yup.string().required("Address is required"),
});
const passwordValidationSchema = Yup.object({
  password: Yup.string()
    .required("Current password is required")
    .min(6, "Password must be at least 6 characters")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  newPassword: Yup.string()
    .required("New password is required")
    .min(6, "Password must be at least 6 characters")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});
interface User {
  employee_id: number;
  name: string;
  email: string;
  phone: string;
  profile_image: string;
  state_id: number;
  city_id: number;
  region_id: number;
  address: string;
}
interface OtpValues {
  email: string;
  otp: string;
}
interface State {
  state_id: number;
  state_name: string;
}
interface City {
  city_id: number;
  city_name: string;
}
interface Region {
  region_id: number;
  region_name: string;
}
interface ProfileFormValues {
  name: string;
  email: string;
  phone: string;
  profile_image?: string;
}
interface AddressFormValues {
  state_id: string | number;
  city_id: string | number;
  region_id: string | number;
  address: string;
}
interface FormikProps {
  values: ProfileFormValues;
  actions: FormikHelpers<ProfileFormValues>;
}
interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
}
const AdminProfile = () => {
  const [userData, setUserData] = useState<User | null>(null);
  const { fetchError, fetchSuccess } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [formikProps, setFormikProps] = useState<FormikProps | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [initialState, setInitialState] = useState<State | null>(null);
  const [initialCity, setInitialCity] = useState<City | null>(null);
  const [initialRegion, setInitialRegion] = useState<Region | null>(null);
  const [themeInput, setThemeInput] = useState("");
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeError, setThemeError] = useState<string | null>(null);
  const themeContext = useThemeColor();
  const isAdmin = user?.roleId === 2;
  React.useEffect(() => {
    if (themeContext?.themeColor) setThemeInput(themeContext.themeColor);
  }, [themeContext?.themeColor]);
  const handleSaveTheme = async () => {
    const hex = /^#[0-9A-Fa-f]{6}$/;
    if (!themeInput || !hex.test(themeInput)) {
      setThemeError("Enter a valid hex color (e.g. #2e7d32)");
      return;
    }
    setThemeError(null);
    setThemeSaving(true);
    try {
      await axios.put("/clients/theme", { theme_color: themeInput });
      themeContext?.setThemeColor(themeInput);
      fetchSuccess("Theme updated successfully");
    } catch (err) {
      const data = (err as AxiosError)?.response?.data as { error?: string } | undefined;
      const msg = data?.error || "Failed to update theme";
      setThemeError(msg);
      fetchError(msg);
    } finally {
      setThemeSaving(false);
    }
  };
  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/employee/${user?.userId}`);
      const { data } = response;
      setSelectedState(data?.state_id);
      setSelectedCity(data?.city_id);
      setInitialState({
        state_id: data?.state_id,
        state_name: data?.state_name,
      });
      setInitialCity({
        city_id: data?.city_id,
        city_name: data?.city_name,
      });
      setInitialRegion({
        region_id: data?.region_id,
        region_name: data?.region_name,
      });
      setUserData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [user, setSelectedState, setSelectedCity]);

  useEffect(() => {
    fetchUserData();
  }, [user, fetchUserData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleOpenOtpModal = () => {
    setOtpModalOpen(true);
  };

  const handleCloseOtpModal = () => {
    setOtpModalOpen(false);
  };

  const handleSendOtp = async (email: string) => {
    try {
      await axios.post("/otp/send-otp", { email });
      handleOpenOtpModal();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleOtpSubmit = async (values: OtpValues) => {
    try {
      const response = await axios.put(`/employee/${userData?.employee_id}`, {
        ...values,
        otp,
      });
      if (response.status === 200) {
        handleCloseOtpModal();
        fetchSuccess("Email updated successfully");
        fetchUserData();
      }
    } catch (error) {
      fetchError("OTP verification failed");
      throw error;
    }
  };
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      // Append new files to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
  });
  useEffect(() => {
    const filesWithPreview = dropzone.acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    ) as UploadedFile[];

    setUploadedFiles(filesWithPreview);
  }, [dropzone.acceptedFiles]);
  const handleAxiosError = (error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response) {
      fetchError(error.response.data.error);
    } else {
      fetchError(fallback);
    }
  };
  const updateProfile = async (
    employeeId: number | undefined,
    values: ProfileFormValues
  ) => {
    const response = await axios.put(`/employee/${employeeId}`, values);
    fetchSuccess(response.data.message);
    fetchUserData();
  };
  const uploadImageAndUpdateProfile = async (
    file: UploadedFile,
    values: ProfileFormValues,
    employeeId?: number
  ) => {
    const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;

    if (!bucketName) {
      fetchError("AWS bucket configuration is missing");
      return;
    }

    const fileName = `${Date.now()}-${file.name}`;
    values.profile_image = fileName;

    const params: AWS.S3.PutObjectRequest = {
      Bucket: bucketName,
      Key: fileName,
      Body: file,
      ContentType: file.type,
      ACL: "public-read",
    };

    s3.upload(params, async (err) => {
      if (err) {
        console.error("Error uploading file:", err);
        fetchError("Failed to upload image");
        return;
      }

      try {
        await updateProfile(employeeId, values);
      } catch (error) {
        handleAxiosError(error, "Failed to update profile");
      }
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading ? (
        <CircularProgress />
      ) : (
        <Card
          sx={{
            maxWidth: "100vw",
            width: "100%",
            padding: "24px",
            maxHeight: "70%",
          }}
        >
          <SoftBox mt={5}>
            <Grid justifyContent="center">
              <Grid item xs={12} md={8} lg={6}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={2}
                >
                  <Grid item>
                    <Typography variant="h5" component="div" gutterBottom>
                      Profile Information
                    </Typography>
                  </Grid>
                </Grid>
                <Box className="profile-tabs">
                  <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="Personal Information" />
                    <Tab label="Update Address" />
                    <Tab label="Change Password" />
                    {isAdmin && <Tab label="Theme" />}
                  </Tabs>
                  <Box mt={3}>
                    {tabIndex === 0 && (
                      <Formik
                        enableReinitialize
                        initialValues={{
                          name: userData?.name || "",
                          email: userData?.email || "",
                          phone: userData?.phone || "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (
                          values: ProfileFormValues,
                          actions: FormikHelpers<ProfileFormValues>
                        ) => {
                          if (values.email !== userData?.email) {
                            setNewEmail(values.email);
                            setFormikProps({ values, actions });
                            handleSendOtp(values.email);
                            return;
                          }

                          try {
                            if (uploadedFiles?.length > 0) {
                              await uploadImageAndUpdateProfile(
                                uploadedFiles[0],
                                values,
                                userData?.employee_id
                              );
                            } else {
                              await updateProfile(
                                userData?.employee_id,
                                values
                              );
                            }
                          } catch (error) {
                            handleAxiosError(error, "Failed to update profile");
                          }
                        }}
                      >
                        {({ values, errors, touched, handleChange }) => (
                          <Form>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  placeholder="Name"
                                  name="name"
                                  value={values.name}
                                  onChange={handleChange}
                                  error={!!errors.name && touched.name}
                                  helperText={touched.name && errors.name}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  placeholder="Email"
                                  name="email"
                                  value={values.email}
                                  onChange={handleChange}
                                  error={!!errors.email && touched.email}
                                  helperText={touched.email && errors.email}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  placeholder="Phone Number"
                                  inputProps={{ maxLength: 10 }}
                                  name="phone"
                                  value={values.phone}
                                  onChange={handleChange}
                                  error={!!errors.phone && touched.phone}
                                  helperText={touched.phone && errors.phone}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={12}
                                className="profile-section"
                              >
                                {uploadedFiles.length > 0 &&
                                uploadedFiles[0]?.preview ? (
                                  <Avatar
                                    className="profile-img"
                                    sx={{
                                      width: { xs: 84, lg: 84 },
                                      height: { xs: 84, lg: 84 },
                                      cursor: "pointer",
                                    }}
                                    src={uploadedFiles[0]?.preview}
                                  />
                                ) : (
                                  <Avatar
                                    className="profile-img"
                                    sx={{
                                      width: { xs: 84, lg: 84 },
                                      height: { xs: 84, lg: 84 },
                                      cursor: "pointer",
                                    }}
                                    src={
                                      userData?.profile_image
                                        ? `${process.env.REACT_APP_CLOUD_FRONT_URL}/${userData?.profile_image}`
                                        : "/assets/images/team-3.jpg"
                                    }
                                  />
                                )}
                                <Box
                                  className="profile-edit"
                                  sx={{
                                    cursor: "pointer",
                                    width: "fit-content",
                                  }}
                                  {...dropzone.getRootProps()}
                                >
                                  <Box>
                                    <label htmlFor="image-upload-input">
                                      <input
                                        {...dropzone.getInputProps({
                                          name: "profile_image",
                                        })}
                                      />
                                      <IconButton
                                        aria-label="edit"
                                        className="edit-icon"
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </label>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Button
                                  className="success-btn"
                                  type="submit"
                                  variant="contained"
                                  color="success"
                                >
                                  Save Changes
                                </Button>
                              </Grid>
                            </Grid>
                          </Form>
                        )}
                      </Formik>
                    )}
                    {tabIndex === 1 && (
                      <Formik
                        initialValues={{
                          state_id: userData?.state_id || "",
                          city_id: userData?.city_id || "",
                          region_id: userData?.region_id || "",
                          address: userData?.address || "",
                        }}
                        validationSchema={addressValidationSchema}
                        onSubmit={async (values: AddressFormValues) => {
                          const { state_id, city_id, ...rest } = values;

                          const payload = {
                            ...rest,
                            email: userData?.email,
                          };

                          try {
                            await axios.put(
                              `/employee/${userData?.employee_id}`,
                              payload
                            );
                            fetchSuccess("Address updated successfully");
                            fetchUserData();
                          } catch (error) {
                            if (error instanceof AxiosError && error.response) {
                              fetchError(error.response.data.error);
                            } else {
                              fetchError("Failed to update address");
                            }
                          }
                        }}
                      >
                        {({
                          values,
                          handleChange,
                          errors,
                          touched,
                          setFieldValue,
                        }) => (
                          <Form className="update-address-popup">
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                  <label>
                                    <SoftTypography
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      State
                                    </SoftTypography>
                                  </label>
                                  <Autocomplete
                                    id="state-autocomplete"
                                    options={states ?? []}
                                    getOptionLabel={(option) =>
                                      option.state_name || ""
                                    }
                                    value={
                                      values.state_id ? initialState : null
                                    }
                                    onChange={(event, newValue) => {
                                      setFieldValue("city_id", "");
                                      setFieldValue("region_id", "");

                                      setFieldValue(
                                        "state_id",
                                        newValue ? newValue.state_id : ""
                                      );
                                      setInitialState(newValue ?? null);

                                      setSelectedState(
                                        newValue ? newValue.state_id : null
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        name="state_id"
                                        variant="outlined"
                                        error={
                                          !!errors.state_id && touched.state_id
                                        }
                                        helperText={
                                          touched.state_id && errors.state_id
                                        }
                                        fullWidth
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                  <label>
                                    <SoftTypography
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      City
                                    </SoftTypography>
                                  </label>
                                  <Autocomplete
                                    id="city-autocomplete"
                                    options={cities ?? []}
                                    getOptionLabel={(option) =>
                                      option.city_name || ""
                                    }
                                    value={values.city_id ? initialCity : null}
                                    onChange={(event, newValue) => {
                                      setFieldValue("region_id", "");
                                      setFieldValue(
                                        "city_id",
                                        newValue ? newValue.city_id : ""
                                      );
                                      setInitialCity(newValue ?? null);

                                      setSelectedCity(
                                        newValue ? newValue.city_id : null
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        name="city_id"
                                        variant="outlined"
                                        error={
                                          !!errors.city_id && touched.city_id
                                        }
                                        helperText={
                                          touched.city_id && errors.city_id
                                        }
                                        fullWidth
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                                <FormControl fullWidth>
                                  <label>
                                    <SoftTypography
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      Area
                                    </SoftTypography>
                                  </label>
                                  <Autocomplete
                                    id="region-autocomplete"
                                    options={areas ?? []}
                                    getOptionLabel={(option) =>
                                      option.region_name || ""
                                    }
                                    value={
                                      values.region_id ? initialRegion : null
                                    }
                                    onChange={(event, newValue) => {
                                      setFieldValue(
                                        "region_id",
                                        newValue ? newValue.region_id : ""
                                      );
                                      setInitialRegion(newValue ?? null);
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        name="region_id"
                                        variant="outlined"
                                        error={
                                          !!(
                                            errors.region_id &&
                                            touched.region_id
                                          )
                                        }
                                        helperText={
                                          touched.region_id && errors.region_id
                                        }
                                        fullWidth
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                                <label>
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    Address
                                  </SoftTypography>
                                </label>
                                <TextField
                                  fullWidth
                                  placeholder="Address"
                                  name="address"
                                  value={values.address}
                                  onChange={handleChange}
                                  error={!!(errors.address && touched.address)}
                                  helperText={touched.address && errors.address}
                                />
                              </Grid>
                              <Grid item xs={12} md={12}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Button
                                    className="success-btn"
                                    sx={{
                                      position: "relative",
                                      minWidth: 100,
                                    }}
                                    variant="contained"
                                    type="submit"
                                    color="success"
                                  >
                                    Update
                                  </Button>

                                  <Button
                                    className="cancel-btn"
                                    sx={{
                                      position: "relative",
                                      minWidth: 100,
                                      ml: 2.5,
                                    }}
                                    variant="contained"
                                    color="warning"
                                    onClick={() => {
                                      setTabIndex(0);
                                      fetchUserData();
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              </Grid>
                            </Grid>
                          </Form>
                        )}
                      </Formik>
                    )}
                    {tabIndex === 2 && (
                      <Formik
                        initialValues={{
                          password: "",
                          newPassword: "",
                        }}
                        validationSchema={passwordValidationSchema}
                        onSubmit={async (values, { resetForm }) => {
                          try {
                            const response = await axios.put(
                              `/employee/password/${user?.userId}`,
                              values
                            );
                            resetForm();
                            fetchSuccess(response.data.message);
                          } catch (error) {
                            if (error instanceof AxiosError && error.response) {
                              fetchError(error.response.data.error);
                            }
                          }
                        }}
                      >
                        {({ values, errors, touched, handleChange }) => (
                          <Form>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className="view-password"
                                  fullWidth
                                  placeholder="Current Password"
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  value={values.password}
                                  onChange={handleChange}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() =>
                                            setShowPassword(!showPassword)
                                          }
                                        >
                                          {showPassword ? (
                                            <VisibilityOff />
                                          ) : (
                                            <Visibility />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={!!errors.password && touched.password}
                                  helperText={
                                    touched.password && errors.password
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  className="view-password"
                                  placeholder="New Password"
                                  type={showNewPassword ? "text" : "password"}
                                  name="newPassword"
                                  value={values.newPassword}
                                  onChange={handleChange}
                                  error={
                                    !!errors.newPassword && touched.newPassword
                                  }
                                  helperText={
                                    touched.newPassword && errors.newPassword
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() =>
                                            setShowNewPassword(!showNewPassword)
                                          }
                                        >
                                          {showNewPassword ? (
                                            <VisibilityOff />
                                          ) : (
                                            <Visibility />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Button
                                  className="success-btn"
                                  type="submit"
                                  variant="contained"
                                  color="success"
                                >
                                  Update Password
                                </Button>
                                <Button
                                  className="cancel-btn"
                                  sx={{
                                    position: "relative",
                                    minWidth: 100,
                                    ml: 2.5,
                                  }}
                                  variant="contained"
                                  color="warning"
                                  onClick={() => setTabIndex(0)}
                                >
                                  Cancel
                                </Button>
                              </Grid>
                            </Grid>
                          </Form>
                        )}
                      </Formik>
                    )}
                    {isAdmin && tabIndex === 3 && themeContext && (
                      <Box>
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                          Theme colour
                        </SoftTypography>
                        <SoftTypography
                          variant="button"
                          color="text"
                          fontWeight="regular"
                          sx={{ display: "block", mb: 2 }}
                        >
                          Change the application primary colour.
                        </SoftTypography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item>
                            <Box
                              component="label"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: "pointer",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                width: 48,
                                height: 42,
                              }}
                            >
                              <input
                                type="color"
                                value={themeInput || themeContext.themeColor || "#2e7d32"}
                                onChange={(e) => {
                                  setThemeInput(e.target.value);
                                  setThemeError(null);
                                }}
                                style={{
                                  width: 56,
                                  height: 56,
                                  padding: 0,
                                  border: "none",
                                  cursor: "pointer",
                                  margin: -8,
                                }}
                                aria-label="Theme colour"
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              size="small"
                              fullWidth
                              value={themeInput || themeContext.themeColor || "#2e7d32"}
                              onChange={(e) => {
                                setThemeInput(e.target.value);
                                setThemeError(null);
                              }}
                              placeholder="#2e7d32"
                              error={Boolean(themeError)}
                              helperText={themeError}
                            />
                          </Grid>
                          <Grid item>
                            <Button
                              className="success-btn"
                              variant="contained"
                              color="success"
                              onClick={handleSaveTheme}
                              disabled={themeSaving}
                            >
                              {themeSaving ? "Saving…" : "Save theme"}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>
      )}
      <Modal
        open={otpModalOpen}
        onClose={handleCloseOtpModal}
        aria-labelledby="otp-modal"
        aria-describedby="otp-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: 4,
            boxShadow: 24,
            p: 4,
            bgcolor: "background.paper",
          }}
        >
          <Typography id="otp-modal" variant="h6" component="h2">
            Enter OTP
          </Typography>
          <TextField
            placeholder="OTP"
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            sx={{ mt: 2, width: "100%" }}
          />
          <Button
            className="success-btn"
            onClick={() =>
              handleOtpSubmit({
                ...formikProps?.values,
                email: newEmail,
                otp,
              })
            }
            variant="contained"
            color="success"
            sx={{ mt: 2 }}
          >
            Submit
          </Button>
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default AdminProfile;
